import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import Dashboard from '@/pages/Dashboard';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import Bookings from '@/pages/Bookings';
import Consultants from '@/pages/Consultants';
import ConsultantProfile from '@/pages/ConsultantProfile';
import PaymentSuccess from '@/pages/PaymentSuccess';
import Payments from '@/pages/Payments';
import Reports from '@/pages/Reports';
import Feedback from '@/pages/Feedback';
import About from '@/pages/About';
import ResourceHub from '@/pages/ResourceHub';
import Admin from '@/pages/Admin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send cookies on every cross-domain request so the httpOnly `abc_auth` cookie
// set by the backend on sign-in is automatically attached to every API call.
// This is what makes httpOnly-cookie auth work — see set_auth_cookie() in server.py.
axios.defaults.withCredentials = true;

export const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

/**
 * Navigate to a protected user-area path. When the visitor is logged out we
 * stash the desired destination in sessionStorage and bounce them to the
 * landing page with ?auth=1 — LandingPage auto-opens its sign-in form on that
 * query, and after a successful login it consumes `postLoginRedirect` and
 * routes the user to where they originally wanted to go.
 *
 * Usage:
 *   const goto = useProtectedNavigate();
 *   <button onClick={() => goto('/consultants')}>Need Expert Guidance?</button>
 */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

export const useProtectedNavigate = () => {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  return useCallback((path) => {
    if (!path) return;
    if (user) {
      navigate(path);
      return;
    }
    try { sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path); } catch (_e) { /* ignore */ }
    navigate('/?auth=1', { replace: false });
  }, [user, navigate]);
};

function App() {
  const [user, setUser] = useState(null);
  // Token is kept in React state only (NOT localStorage) so XSS cannot read it.
  // The httpOnly `abc_auth` cookie set by the backend is what actually keeps
  // the session alive across page refreshes — we bootstrap by calling
  // /auth/me which authenticates via that cookie.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    setUser(null);
    setToken(null);
    // Best-effort: tell the backend to drop the auth cookie. Safe to fail.
    try { await axios.post(`${API}/auth/logout`); } catch (_e) { /* ignore */ }
    // Clear the legacy localStorage entry from older sessions.
    try { localStorage.removeItem('token'); } catch (_e) { /* ignore */ }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    // No explicit Authorization header — the browser sends the httpOnly cookie
    // automatically when axios.defaults.withCredentials is true. If a legacy
    // localStorage token is still around, we'll fall back to it once and then
    // wipe it on the next login.
    try {
      let legacy = null;
      try { legacy = localStorage.getItem('token'); } catch (_e) { /* ignore */ }
      const headers = legacy ? { Authorization: `Bearer ${legacy}` } : {};
      const response = await axios.get(`${API}/auth/me`, { headers });
      setUser(response.data);
    } catch (_error) {
      // 401 here is the normal logged-out path — don't spam the console.
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback((userData, userToken) => {
    setUser(userData);
    setToken(userToken || null);
    // Drop any old localStorage entry — we no longer use it.
    try { localStorage.removeItem('token'); } catch (_e) { /* ignore */ }
  }, []);

  const contextValue = useMemo(() => ({
    user,
    token,
    login,
    logout
  }), [user, token, login, logout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          <p className="mt-4 text-sm text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                user
                  ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} />
                  : <LandingPage />
              }
            />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
            <Route path="/projects" element={user ? <Projects /> : <Navigate to="/" />} />
            <Route path="/projects/:id" element={user ? <ProjectDetail /> : <Navigate to="/" />} />
            <Route path="/bookings" element={user ? <Bookings /> : <Navigate to="/" />} />
            <Route path="/reports" element={user ? <Reports /> : <Navigate to="/" />} />
            <Route path="/payments" element={user ? <Payments /> : <Navigate to="/" />} />
            <Route path="/feedback" element={user ? <Feedback /> : <Navigate to="/" />} />
            <Route path="/consultants" element={user ? <Consultants /> : <Navigate to="/" />} />
            <Route path="/consultants/:id" element={user ? <ConsultantProfile /> : <Navigate to="/" />} />
            <Route path="/services" element={<ResourceHub />} />
            <Route path="/about" element={<About />} />
            <Route path="/resources" element={<Navigate to="/services" replace />} />
            <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/" />} />
            <Route path="/admin" element={user ? <Admin /> : <Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
